import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { JobAlertCreateParams } from '../../../models/JobPostModals';
import { ResponsiveModal } from '../../../components/ResponsiveModal/ResponsiveModal';
import { TextBlock } from '../../../uicomp/atmos/content-block/ContentBlock';
import { Button, Modal, Radio, Space, Switch, Typography } from 'antd';
import { BellOutlined, MailOutlined, MessageOutlined } from '@ant-design/icons';
import { useConnection } from '../../../connection/Application';
import Utils from '../../../utils/Utils';
// import { useRouter } from 'next/router';
import { AlertManagementView } from '../../JobAlertView/AlertManagementView';
import { UiTag } from '../../../components/UIV2/UiTag';

export type JobAlertModalInstance = {
  show: (params: JobAlertCreateParams) => void;
};

type Props = {
  onAlertCreated: (alertId: string) => void;
};
const { Text } = Typography;

const JobAlertModal: ForwardRefRenderFunction<JobAlertModalInstance, Props> = (
  { onAlertCreated },
  ref,
) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<JobAlertCreateParams>();
  const connection = useConnection();
  // const history = useRouter();
  const [emailFrequency, setEmailFrequency] = useState<'weekly' | 'daily' | 'instant'>('instant');
  const [smsFrequency, setSmsFrequency] = useState<'daily' | 'weekly'>('daily');
  const [smsAlertOn, setSmsAlertOn] = useState<boolean>(false);
  const [editAlertVisible, setEditAlertVisible] = useState<boolean>(false);
  useImperativeHandle(
    ref,
    () => ({
      show: (alertParams) => {
        setVisible(true);
        setLoading(false);
        setEmailFrequency('instant');
        setSmsFrequency('daily');
        setSmsAlertOn(false);

        setParams(alertParams);
      },
    }),
    [],
  );

  const createAlert = useCallback(() => {
    setLoading(true);
    const createParams: JobAlertCreateParams = Utils.selectFields(
      params,
      'search',
      'sector',
      'jobCategory',
      'company',
      'location',
      'salary_type',
      'SalaryUp',
      'SalaryDown',
      'graduate',
      'jobType',
      'jobFunction',
    );

    createParams.frequency = emailFrequency;

    createParams.smsAlertOn = smsAlertOn;
    if (smsAlertOn) {
      createParams.smsFrequency = smsFrequency;
    } else {
      createParams.smsFrequency = undefined;
    }

    connection
      .post('job-post/alerts', createParams)
      .then((value) => {
        onAlertCreated(value.alertId);
      })
      .catch(() => {
        Modal.error({ title: 'Failed to create alert', content: 'Something went wrong!' });
      })
      .finally(() => {
        setVisible(false);
        setLoading(false);
      });
  }, [connection, emailFrequency, onAlertCreated, params, smsAlertOn, smsFrequency]);

  return (
    <>
      <ResponsiveModal
        width={500}
        title={
          <Space className={'text-[20px]'}>
            <UiTag
              icon={<BellOutlined />}
              color={'red'}
              label={''}
              className={'text-[20px] !rounded-md w-[35px] flex items-center justify-center'}
            />
            {'Adding Job Alerts!'}
          </Space>
        }
        open={visible}
        footer={
          <div className="flex justify-between w-full my-2">
            <Button
              size={'small'}
              className="w-full mr-1 "
              disabled={loading}
              onClick={() => {
                setVisible(false);
                setEditAlertVisible(true);
              }}>
              View All Alerts
            </Button>
            <Button
              size={'small'}
              loading={loading}
              onClick={createAlert}
              type="primary"
              className="w-full ml-1">
              Confirm
            </Button>
          </div>
        }
        onCancel={() => setVisible(false)}
        onOk={createAlert}>
        <div className="grid grid-cols-2 gap-8 pt-2">
          <div>
            <div className="flex items-center justify-between w-full mb-2 pb-0">
              <Space>
                <MailOutlined className="text-red-500" />
                <Text strong>Email Alerts</Text>
              </Space>
            </div>
            <div className="w-full h-[2px] bg-red-500 mb-4" />
            <Radio.Group
              onChange={(e) => setEmailFrequency(e.target.value)}
              value={emailFrequency}
              className="flex flex-col space-y-4">
              <Radio value="instant">
                <span className="ml-1">Instant</span>
              </Radio>
              <Radio value="daily">
                <span className="ml-1">Daily</span>
              </Radio>
              <Radio value="weekly">
                <span className="ml-1">Weekly</span>
              </Radio>
            </Radio.Group>
          </div>

          <div>
            <div className="flex items-center justify-between w-full mb-2 pb-0 cursor-pointer">
              <Space>
                <MessageOutlined className="text-red-500" />
                <Text strong>SMS Alerts</Text>
              </Space>
              <Switch checked={smsAlertOn} onChange={(checked) => setSmsAlertOn(checked)} />
            </div>
            <div className="w-full h-[2px] bg-red-500 mb-4" />
            <Radio.Group
              onChange={(e) => setSmsFrequency(e.target.value)}
              value={smsFrequency}
              disabled={!smsAlertOn}
              className={`flex flex-col space-y-2 `}>
              <Radio value="daily">
                <span className="ml-1">Daily</span>
              </Radio>
              <Radio value="weekly">
                <span className="ml-1">Weekly</span>
              </Radio>
            </Radio.Group>
          </div>
        </div>
        <style jsx global>{`
          .ant-modal-content {
            border-radius: 16px !important;
          }
        `}</style>
      </ResponsiveModal>
      <ResponsiveModal
        width={1000}
        open={editAlertVisible ?? false}
        title={'Manage Job Alerts'}
        onCancel={() => setEditAlertVisible(false)}
        footer={false}>
        <AlertManagementView />
        <style jsx global>{`
          .ant-modal-content {
            border-radius: 16px !important;
          }
        `}</style>
      </ResponsiveModal>
    </>
  );
};

export const useJobAlertModal = () => useRef<JobAlertModalInstance>(null);

export default React.forwardRef<JobAlertModalInstance, Props>(JobAlertModal);
